import { Capacitor, registerPlugin } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import * as Sentry from '@sentry/react';
import { DEFAULT_THEME, tTheme } from './themeConfig';
import { NATIVE_THEME_COLOR } from './nativeThemeColors';

interface INativeThemeOverride {
  theme: tTheme;
}

interface IAppStatusBarPlugin {
  setLightBackground(options: { light: boolean }): Promise<void>;
  setNavigationBarLight(options: { light: boolean; persist: boolean }): Promise<void>;
}

const AppStatusBar = registerPlugin<IAppStatusBarPlugin>('AppStatusBar');

let appTheme: tTheme = DEFAULT_THEME;
const overrides: INativeThemeOverride[] = [];

let nativeTouched = false;

const reportedMethods = new Set<string>();

// Each failing native status-bar method is reported once per session; non-Error rejections are wrapped.
const reportNativeThemeError =
  (method: string) =>
  (error: unknown): void => {
    if (reportedMethods.has(method)) return;
    reportedMethods.add(method);

    if (error instanceof Error) {
      Sentry.captureException(error, { tags: { area: 'native-theme', method } });
      return;
    }
    Sentry.captureException(new Error(`${method} rejected`), {
      tags: { area: 'native-theme', method },
      extra: { rejection: error }
    });
  };

const paintNativeTheme = (theme: tTheme, fixed: boolean): void => {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', NATIVE_THEME_COLOR[theme]);

  if (!Capacitor.isNativePlatform()) return;

  // Android keeps the last theme for the strip behind the 3-button bar, so every paint syncs it, dark startup included.
  // Only the app theme is kept: a fixed surface must not decide the next cold start.
  if (Capacitor.getPlatform() === 'android') {
    AppStatusBar.setNavigationBarLight({
      light: theme === 'light',
      persist: !fixed
    }).catch(reportNativeThemeError('setNavigationBarLight'));
  }

  // Dark startup makes no status-bar calls; the first call happens when light is shown or the user switches.
  if (!nativeTouched) {
    if (theme !== 'light') return;
    nativeTouched = true;
  }

  const isLight = theme === 'light';

  if (Capacitor.getPlatform() === 'android') {
    AppStatusBar.setLightBackground({ light: isLight }).catch(
      reportNativeThemeError('setLightBackground')
    );
    return;
  }

  // A fixed-dark screen needs light glyphs even when the device is in light mode; Default follows the device.
  const darkStyle = fixed ? Style.Dark : Style.Default;
  const style = isLight ? Style.Light : darkStyle;

  StatusBar.setStyle({ style }).catch(reportNativeThemeError('setStyle'));
};

const paintEffectiveTheme = (): void => {
  const override = overrides[overrides.length - 1];

  paintNativeTheme(override?.theme ?? appTheme, override !== undefined);
};

export const applyNativeTheme = (theme: tTheme): void => {
  appTheme = theme;
  paintEffectiveTheme();
};

// The latest override wins until it is released; releasing any of them restores the next one down.
export const pushNativeThemeOverride = (theme: tTheme): (() => void) => {
  const entry: INativeThemeOverride = { theme };
  overrides.push(entry);
  paintEffectiveTheme();

  return () => {
    const index = overrides.indexOf(entry);
    if (index === -1) return;

    overrides.splice(index, 1);
    paintEffectiveTheme();
  };
};
